/**
 * Saved Comparisons Limit Modal - Blocks comparison creation when limit reached
 *
 * - Non-dismissible modal requiring action
 * - Blurs page content behind modal (except header/nav)
 * - Shows on desktop and mobile
 * - Two actions: View Results or Upgrade Plan
 */

import React from 'react';
import { XCircleIcon } from '@heroicons/react/24/outline';
import type { SavedComparisonsQuota } from '../../services/entitlement';

interface SavedComparisonsLimitModalProps {
  quota: SavedComparisonsQuota;
  planName: string;
  planCode: string;
  onViewResults: () => void;
}

export const SavedComparisonsLimitModal: React.FC<SavedComparisonsLimitModalProps> = ({
  quota,
  planName,
  planCode,
  onViewResults,
}) => {
  // Determine next tier info based on current plan
  const getNextTierInfo = () => {
    switch (planCode) {
      case 'edge':
        return { name: 'Flow', limit: 500 };
      case 'flow':
        return { name: 'Nexus', limit: 5000 };
      default:
        return { name: 'higher tier', limit: 'more' };
    }
  };

  const nextTier = getNextTierInfo();

  return (
    <>
      {/* Backdrop with blur - positioned below header to keep header accessible */}
      <div className="fixed inset-0 bg-black/50 backdrop-blur-md z-[9990]" />

      {/* Modal - centered and non-dismissible, positioned below header */}
      <div className="fixed inset-0 z-[9991] flex items-center justify-center p-4">
        <div
          className="bg-slate-800 border border-slate-700 rounded-xl shadow-2xl max-w-md w-full p-6"
          role="dialog"
          aria-modal="true"
          aria-labelledby="limit-modal-title"
        >
          {/* Icon and Title */}
          <div className="flex flex-col items-center text-center mb-6">
            <div className="flex items-center justify-center w-16 h-16 bg-red-900/20 rounded-full mb-4">
              <XCircleIcon className="w-10 h-10 text-red-400" />
            </div>
            <h2
              id="limit-modal-title"
              className="text-2xl font-bold text-white mb-2"
            >
              Storage Limit Reached
            </h2>
          </div>

          {/* Message */}
          <div className="text-center mb-6">
            <p className="text-slate-300 mb-4">
              You've reached your saved comparisons limit
            </p>
            <p className="text-2xl font-bold text-white mb-6">
              ({quota.count} / {quota.limit})
            </p>

            <div className="text-left bg-slate-900/50 rounded-lg p-4 mb-4">
              <p className="text-slate-300 text-sm mb-3">
                To create more comparisons, you can:
              </p>
              <ul className="text-slate-300 text-sm space-y-2">
                <li className="flex items-start">
                  <span className="text-cyan-400 mr-2">•</span>
                  <span>Delete old comparisons to free up space</span>
                </li>
                <li className="flex items-start">
                  <span className="text-cyan-400 mr-2">•</span>
                  <span>Upgrade your plan for more storage</span>
                </li>
              </ul>
            </div>

            <div className="text-sm text-slate-400 bg-slate-900/30 rounded-lg p-3">
              <p className="mb-1">
                <span className="font-medium text-slate-300">Your current plan:</span>{' '}
                {planName} {planCode === 'edge' && '(Free)'}
              </p>
              <p>
                <span className="font-medium text-slate-300">Upgrade to {nextTier.name}</span>{' '}
                for {nextTier.limit} saved comparisons
              </p>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row gap-3">
            <button
              onClick={onViewResults}
              className="flex-1 px-4 py-3 bg-slate-700 hover:bg-slate-600 text-white font-medium rounded-lg transition-colors"
            >
              View Results
            </button>
            <a
              href="https://www.smartprompt.us/billing/upgrade"
              className="flex-1 px-4 py-3 bg-cyan-600 hover:bg-cyan-700 text-white font-medium rounded-lg transition-colors text-center"
            >
              Upgrade Plan →
            </a>
          </div>
        </div>
      </div>
    </>
  );
};
